import React, { useState, useRef, useEffect } from "react";
import Icon from "./Icon";

/**
 * CreateDocumentModal
 * 
 * Step 1: Choose document type (Word, PDF, Excel, PowerPoint)
 * Step 2: Fill in content based on type
 * Step 3: Create -> file appears in folder
 * 
 * Real mode: calls ctodocx-createDocument Lambda
 */

const DOC_TYPES = [
  {
    id: "docx",
    name: "Word Document",
    icon: "fileWord",
    color: "#4285f4",
    description: "Create a text document with title and content",
  },
  {
    id: "pdf",
    name: "PDF Document",
    icon: "filePdf",
    color: "#ea4335",
    description: "Create a PDF with title, content, and formatting",
  },
  {
    id: "xlsx",
    name: "Excel Spreadsheet",
    icon: "fileExcel",
    color: "#34a853",
    description: "Create a spreadsheet with headers and rows",
  },
  {
    id: "pptx",
    name: "PowerPoint Presentation",
    icon: "filePpt",
    color: "#fb8c00",
    description: "Create a presentation with slides",
  },
];

function CreateDocumentModal({ onClose, onCreate, currentPath }) {
  const [step, setStep] = useState(1); // 1: choose type, 2: fill content
  const [selectedType, setSelectedType] = useState(null);
  const [creating, setCreating] = useState(false);

  // Document content state
  const [docTitle, setDocTitle] = useState("");
  const [docContent, setDocContent] = useState("");

  // Excel state
  const [headers, setHeaders] = useState(["Column A", "Column B", "Column C"]);
  const [rows, setRows] = useState([
    ["", "", ""],
    ["", "", ""],
    ["", "", ""],
  ]);

  // PPTX state
  const [slides, setSlides] = useState([
    { title: "", content: "" },
  ]);

  const titleRef = useRef(null);

  useEffect(() => {
    if (step === 2) {
      setTimeout(() => titleRef.current?.focus(), 100);
    }
  }, [step]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleCreate = async () => {
    if (!docTitle.trim()) return;
    setCreating(true);

    const fileName = `${docTitle.trim()}.${selectedType.id}`;
    const fileSize = estimateSize(selectedType.id, docContent, rows, slides);

    const newFile = {
      type: "file",
      name: fileName,
      ext: selectedType.id,
      size: fileSize,
      modified: new Date().toISOString().split("T")[0],
      owner: "You",
    };

    onCreate(newFile);
    setCreating(false);
    onClose();
  };

  // Render Step 1: Type Selection
  if (step === 1) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="create-doc-modal" onClick={(e) => e.stopPropagation()}>
          <div className="create-doc-header">
            <h3>
              <Icon name="filePlus" style={{ color: "var(--accent)", marginRight: 8 }} />
              Create New Document
            </h3>
            <button className="preview-close" onClick={onClose}>
              <Icon name="x" />
            </button>
          </div>

          <p className="create-doc-subtitle">Choose a document type to get started</p>

          <div className="doc-type-grid">
            {DOC_TYPES.map((type) => (
              <div
                key={type.id}
                className="doc-type-card"
                onClick={() => handleTypeSelect(type)}
              >
                <Icon name={type.icon} size={28} style={{ color: type.color }} />
                <div className="doc-type-info">
                  <h4>{type.name}</h4>
                  <p>{type.description}</p>
                </div>
                <Icon name="arrowRight" className="doc-type-arrow" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render Step 2: Content Editor
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-doc-modal editor-mode" onClick={(e) => e.stopPropagation()}>
        <div className="create-doc-header">
          <div className="create-doc-header-left">
            <button className="back-btn" onClick={() => setStep(1)}>
              <Icon name="arrowLeft" />
            </button>
            <Icon name={selectedType.icon} size={20} style={{ color: selectedType.color }} />
            <h3>New {selectedType.name}</h3>
          </div>
          <button className="preview-close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>

        <div className="create-doc-body">
          {/* File name */}
          <div className="doc-field">
            <label>File Name</label>
            <div className="doc-filename-input">
              <input
                ref={titleRef}
                type="text"
                placeholder="Enter document name..."
                value={docTitle}
                onChange={(e) => setDocTitle(e.target.value)}
                maxLength={80}
              />
              <span className="doc-ext">.{selectedType.id}</span>
            </div>
          </div>

          {/* Content based on type */}
          {(selectedType.id === "docx" || selectedType.id === "pdf") && (
            <div className="doc-field">
              <label>Content</label>
              <textarea
                className="doc-textarea"
                placeholder="Type your document content here..."
                value={docContent}
                onChange={(e) => setDocContent(e.target.value)}
                rows={8}
              />
            </div>
          )}

          {selectedType.id === "xlsx" && (
            <div className="doc-field">
              <label>Spreadsheet Data</label>
              <div className="excel-editor">
                <table className="excel-table">
                  <thead>
                    <tr>
                      {headers.map((h, i) => (
                        <th key={i}>
                          <input
                            type="text"
                            value={h}
                            onChange={(e) => {
                              const newH = [...headers];
                              newH[i] = e.target.value;
                              setHeaders(newH);
                            }}
                          />
                        </th>
                      ))}
                      <th className="add-col" onClick={() => {
                        setHeaders([...headers, `Column ${String.fromCharCode(65 + headers.length)}`]);
                        setRows(rows.map(r => [...r, ""]));
                      }}>+</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci}>
                            <input
                              type="text"
                              value={cell}
                              onChange={(e) => {
                                const newRows = rows.map(r => [...r]);
                                newRows[ri][ci] = e.target.value;
                                setRows(newRows);
                              }}
                            />
                          </td>
                        ))}
                        <td></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button className="add-row-btn" onClick={() => setRows([...rows, new Array(headers.length).fill("")])}>
                  <Icon name="plus" /> Add Row
                </button>
              </div>
            </div>
          )}

          {selectedType.id === "pptx" && (
            <div className="doc-field">
              <label>Slides</label>
              <div className="slides-editor">
                {slides.map((slide, i) => (
                  <div className="slide-card" key={i}>
                    <div className="slide-number">Slide {i + 1}</div>
                    <input
                      type="text"
                      className="slide-title-input"
                      placeholder="Slide title..."
                      value={slide.title}
                      onChange={(e) => {
                        const newSlides = [...slides];
                        newSlides[i] = { ...newSlides[i], title: e.target.value };
                        setSlides(newSlides);
                      }}
                    />
                    <textarea
                      className="slide-content-input"
                      placeholder="Slide content / bullet points..."
                      value={slide.content}
                      onChange={(e) => {
                        const newSlides = [...slides];
                        newSlides[i] = { ...newSlides[i], content: e.target.value };
                        setSlides(newSlides);
                      }}
                      rows={3}
                    />
                    {slides.length > 1 && (
                      <button className="remove-slide-btn" onClick={() => setSlides(slides.filter((_, idx) => idx !== i))}>
                        <Icon name="trash" />
                      </button>
                    )}
                  </div>
                ))}
                <button className="add-slide-btn" onClick={() => setSlides([...slides, { title: "", content: "" }])}>
                  <Icon name="plus" /> Add Slide
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="create-doc-footer">
          <div className="create-doc-path">
            <Icon name="folder" style={{ color: "#5f6368" }} />
            <span>Saving to: {currentPath === "/" ? "All Files" : currentPath}</span>
          </div>
          <div className="create-doc-actions">
            <button className="modal-btn cancel" onClick={onClose}>Cancel</button>
            <button
              className="modal-btn create"
              onClick={handleCreate}
              disabled={!docTitle.trim() || creating}
            >
              {creating ? (
                <><Icon name="spinner" spin /> Creating...</>
              ) : (
                <><Icon name="check" /> Create Document</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function estimateSize(type, content, rows, slides) {
  switch (type) {
    case "docx": return `${Math.max(12, Math.floor(content.length / 100) * 10 + 12)} KB`;
    case "pdf": return `${Math.max(15, Math.floor(content.length / 80) * 12 + 15)} KB`;
    case "xlsx": return `${Math.max(8, rows.length * 3 + 8)} KB`;
    case "pptx": return `${Math.max(45, slides.length * 120)} KB`;
    default: return "10 KB";
  }
}

export default CreateDocumentModal;